import { Button } from "@comps/ui/button";
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@comps/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { AggregationType, aggregationTypeToIcon, FieldType, fieldTypeIcon, validAggregationByType } from "@dto/fields";
import { View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { clearData } from "@utils/data";
import { reorderFields } from "@utils/model";
import { buildViewPayload, currentAggregation, draftFieldKeys, draftRows, dropLineRow, DropPosition, FieldEntry, hasAggregationChanges, hasOrderChanges, hasVisibleField, hideAll, initDraft, lockedPathKeys, OrganizeFieldRow, OrganizeFieldsDraft, pinnedPathKeys, setAggregation, showAll, toggleField } from "@utils/organizeFields";
import { toastError } from "@utils/toasts";
import { getWsButtonStyle, TW_ButtonList, TW_FieldMenuPopoverContent, TW_OrganizeFieldsAggregationNone, TW_OrganizeFieldsAggregationTitle, TW_OrganizeFieldsSeparator, TW_OrganizeFieldsButton, TW_OrganizeFieldsDialogContent, TW_OrganizeFieldsDescriptionLine, TW_OrganizeFieldsDropLine, TW_OrganizeFieldsEye, TW_OrganizeFieldsFooter, TW_OrganizeFieldsGrip, TW_OrganizeFieldsGripPlaceholder, TW_OrganizeFieldsIcon, TW_OrganizeFieldsLink, TW_OrganizeFieldsLinks, TW_OrganizeFieldsList, TW_OrganizeFieldsName, TW_OrganizeFieldsNameHidden, TW_OrganizeFieldsNameSuffix, TW_OrganizeFieldsRow, TW_OrganizeFieldsRowDragging, TW_OrganizeFieldsTypeIcon, TW_OrganizeFieldsWarning, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Eye, EyeSlash } from "iconsax-react";
import { GripVertical, Minus, TriangleAlert } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import AggregationMenuItem from "./AggregationMenuItem";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { useOrganizeFieldsDrag } from "./useOrganizeFieldsDrag";

// What the dialog organizes: the fields of a view (order, visibility,
// aggregations of the view), or the fields of the object itself, from its
// configuration (order only: hiding a field is only possible in a view, and the
// default aggregation of a field is set on the field)
export type OrganizeFieldsMode = 'view' | 'object';

interface OrganizeFieldsDialogContentProps {
  mode: OrganizeFieldsMode;
  // Fields of the object, when the dialog opened
  entries: FieldEntry[];
  // Path keys the view showed when the dialog opened
  shownPathKeys: string[];
  // The user cannot change the view (or the fields of the object): the fields are only listed
  readOnly: boolean;
  // Told when the view starts and stops being saved: the dialog cannot be closed meanwhile
  onSavingChange: (saving: boolean) => void;
  onClose: () => void;
}

/**
 * Content of the "Organize fields" dialog of a view: the fields are listed in
 * the order of the columns of the grid, the user reorders them (drag & drop of
 * their row), shows or hides them and changes their aggregation in a draft. The
 * view is saved once, when the draft is validated; cancelling the dialog drops
 * the draft.
 * From the configuration of an object (no view), the fields of the object are
 * only reordered: their order is saved in a single request.
 * The content is mounted when the dialog opens: the draft starts from the view
 * as it is then (nothing can change the view while the modal dialog is open).
 */
function OrganizeFieldsDialogContent({ mode, entries, shownPathKeys, readOnly, onSavingChange, onClose }: OrganizeFieldsDialogContentProps) {
  const { t } = useTranslation();
  const selectedView = useModelStore(state => state.selectedView);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const updateSelectedView = useModelStore(state => state.updateSelectedView);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos);
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  // In the configuration of the object, the fields in error are ordered as the
  // other ones: their order is a property of the field, they need no path for it
  const [initial] = useState<OrganizeFieldsDraft>(() => initDraft(entries, selectedView, shownPathKeys, mode === 'object'));
  const [draft, setDraft] = useState<OrganizeFieldsDraft>(initial);
  const [saving, setSaving] = useState(false);

  const locks = useMemo(() => lockedPathKeys(selectedView, entries), [selectedView, entries]);
  const pinned = useMemo(() => pinnedPathKeys(locks), [locks]);
  const rows = draftRows(entries, selectedView, draft);
  const movableRows = rows.filter(row => row.movable);
  const isObject = mode === 'object';
  const canEdit = !readOnly && !saving;
  // No field can be hidden from the configuration of an object
  const canSave = isObject || hasVisibleField(draft);

  const listRef = useRef<HTMLUListElement>(null);

  const drag = useOrganizeFieldsDrag({
    draft,
    pinned,
    enabled: canEdit,
    lastMovableKey: movableRows.length > 0 ? movableRows[movableRows.length - 1].pathKey : null,
    onMove: setDraft,
  });
  const dropLine = dropLineRow(rows, drag.dropTarget);

  const onToggle = (row: OrganizeFieldRow) => {
    if (canEdit && !row.lock && !row.isError) {
      setDraft(current => toggleField(current, row.pathKey, locks));
    }
  };

  const onAggregationChange = (row: OrganizeFieldRow, aggregation: AggregationType | null) => {
    if (canEdit && !row.isError) {
      setDraft(current => setAggregation(current, row, aggregation));
    }
  };

  // Reorders the fields of the object, all at once
  const onSaveObject = () => {
    if (!workspace?._key || !selectedObjectInfos?.object?._key) {
      return;
    }
    if (!hasOrderChanges(initial, draft)) {
      onClose();
      return;
    }
    setSaving(true);
    onSavingChange(true);
    reorderFields(workspace._key, selectedObjectInfos.object._key, draftFieldKeys(entries, draft),
      (fields) => {
        setSaving(false);
        onSavingChange(false);
        // The grid of the configuration follows the order of the fields of the object
        updateSelectedObjectInfos({ ...selectedObjectInfos, fields }, null, null, sharedObjectInfos);
        onClose();
      },
      () => {
        // The dialog stays open with its draft: the user can try again or cancel
        setSaving(false);
        onSavingChange(false);
        listRef.current?.focus();
      },
      t);
  };

  const onSave = () => {
    if (isObject) {
      onSaveObject();
      return;
    }
    if (!selectedView || !workspace?._key || !selectedObjectInfos?.object?._key) {
      return;
    }
    const payload = buildViewPayload(selectedView, initial, draft);
    if (!payload) {
      onClose();
      return;
    }
    setSaving(true);
    onSavingChange(true);
    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos.object._key}/views`, payload).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        // The dialog stays open with its draft: the user can try again or cancel.
        // The focus, lost when Ok was disabled, goes back to the list.
        onSavingChange(false);
        listRef.current?.focus();
        return;
      }
      updateSelectedView(selectedObjectInfos, payload, null, sharedObjectInfos);
      if (hasAggregationChanges(initial, draft)) {
        // The grouping of the lines changes: they are dropped before being
        // fetched again, as from the menu of a column
        clearData();
      }
      onSavingChange(false);
      onClose();
    }).finally(() => setSaving(false));
  };

  const scrollStyle = {
    scrollbarColor: wsColor + " " + wsLightColor
  };

  return (
    <DialogContent
      // The close button is hidden while saving: the view is saved anyway once the request is sent
      className={TW_OrganizeFieldsDialogContent + (saving ? " [&>button]:hidden" : "")}
      // The focus goes to the list (trapped in the dialog) rather than to the first eye
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        listRef.current?.focus();
      }}
      // A click next to the dialog must not drop the changes of the draft
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => {
        if (saving) {
          e.preventDefault();
        }
      }}
    >
      <DialogHeader className="space-y-2">
        <DialogTitle>{t("object.header.dialog.organize.fields.title")}</DialogTitle>
        <DialogDescription>
          {readOnly
            ? t(isObject ? "view.header.organize.fields.object.forbidden" : "object.header.button.edit.view.forbidden.tooltip")
            : <>
              <span className={TW_OrganizeFieldsDescriptionLine}>{t(isObject ? "object.header.dialog.organize.fields.description.object" : "object.header.dialog.organize.fields.description")}</span>
            </>}
        </DialogDescription>
      </DialogHeader>
      {/* Text-only tooltips, closed as soon as the pointer leaves their icon: kept
          open on the way to the bubble, the one of an eye would cover the
          aggregation icon next to it and keep its tooltip from opening */}
      <TooltipProvider delayDuration={100} disableHoverableContent>
        <ul
          ref={listRef}
          tabIndex={-1}
          aria-label={t(isObject ? "view.header.organize.fields.list.object" : "view.header.organize.fields.list")}
          className={TW_OrganizeFieldsList}
          style={scrollStyle}
          {...drag.listDragProps}
        >
          {rows.map(row => (
            <OrganizeFieldsRow
              key={row.field._key}
              row={row}
              mode={mode}
              readOnly={readOnly}
              canEdit={canEdit}
              dragging={drag.draggedKey === row.pathKey}
              dropPosition={dropLine?.key === row.pathKey ? dropLine.position : null}
              dropLineColor={wsColor}
              rowDragProps={row.movable && !readOnly ? drag.rowDragProps(row.pathKey) : null}
              aggregation={currentAggregation(draft, row)}
              onToggle={() => onToggle(row)}
              onAggregationChange={(aggregation) => onAggregationChange(row, aggregation)}
            />
          ))}
        </ul>
      </TooltipProvider>
      {!isObject && <div className={TW_OrganizeFieldsFooter}>
        {!canSave && !readOnly && <p className={TW_OrganizeFieldsWarning} role="alert">{t("view.header.organize.fields.none.shown")}</p>}
        <div className={TW_OrganizeFieldsLinks}>
          <button type="button" className={TW_OrganizeFieldsLink} onClick={() => setDraft(current => showAll(current))} disabled={!canEdit}>
            {t("view.header.organize.fields.show.all")}
          </button>
          <button type="button" className={TW_OrganizeFieldsLink} onClick={() => setDraft(current => hideAll(current, locks))} disabled={!canEdit}>
            {t("view.header.organize.fields.hide.all")}
          </button>
        </div>
      </div>}
      <div className={TW_ButtonList}>
        {readOnly
          ? <Button className={TW_OrganizeFieldsButton} onClick={onClose} {...okHover}>{t("general.button.close.text")}</Button>
          : <>
            <Button className={TW_OrganizeFieldsButton} onClick={onClose} {...cancelHover} disabled={saving}>{t("general.button.cancel.text")}</Button>
            <Button className={TW_OrganizeFieldsButton} onClick={onSave} {...okHover} disabled={saving || !canSave}>{t("general.button.ok.text")}</Button>
          </>}
      </div>
    </DialogContent>
  );
}

export default OrganizeFieldsDialogContent;


interface OrganizeFieldsRowProps {
  row: OrganizeFieldRow;
  // In the configuration of an object, the fields have no eye and their aggregation cannot change
  mode: OrganizeFieldsMode;
  readOnly: boolean;
  canEdit: boolean;
  dragging: boolean;
  // Side of the row the dragged field would be dropped on
  dropPosition: DropPosition | null;
  dropLineColor: string;
  // Drag & drop props of the row, null when the field cannot move: the whole
  // row is dragged (its handle, its name...), a click on the name still toggles it
  rowDragProps: React.HTMLAttributes<HTMLElement> & { draggable: boolean } | null;
  // Aggregation applied to the field in the draft
  aggregation: AggregationType | null;
  onToggle: () => void;
  onAggregationChange: (aggregation: AggregationType | null) => void;
}

// One field of the dialog: [handle] [eye] [aggregation] | [type icon] [name] [suffix]
// (no eye in the configuration of an object)
function OrganizeFieldsRow({ row, mode, readOnly, canEdit, dragging, dropPosition, dropLineColor, rowDragProps, aggregation, onToggle, onAggregationChange }: OrganizeFieldsRowProps) {
  const { t } = useTranslation();
  const name = row.field.name;
  const isObject = mode === 'object';
  const toggleable = !isObject && canEdit && !row.lock && !row.isError;
  // A field in error is never hidden by the grid: it is neither shown nor hidden here
  const struck = !row.visible && !row.isError;
  const TypeIcon = fieldTypeIcon(row.field.type);

  let lockTooltip: string | null = null;
  if (row.lock === 'horizontalGroup') {
    lockTooltip = t("view.header.organize.fields.group.locked.tooltip");
  } else if (row.lock) {
    lockTooltip = t("view.header.organize.fields.pivot.locked.tooltip");
  }

  const eyeLabel = row.visible
    ? translate(t, "view.header.organize.fields.hide.field.1", [name])
    : translate(t, "view.header.organize.fields.show.field.1", [name]);
  const eyeIcon = row.visible ? <Eye className={TW_OrganizeFieldsIcon} /> : <EyeSlash className={TW_OrganizeFieldsIcon} />;

  let eye: React.ReactNode;
  if (row.isError) {
    eye = <TriangleAlert className={TW_OrganizeFieldsIcon + " text-amber-500"} aria-hidden="true" />;
  } else if (lockTooltip && !readOnly) {
    // aria-disabled rather than disabled: the eye stays focusable, and its tooltip reachable
    eye = (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className={TW_OrganizeFieldsEye} aria-label={eyeLabel} aria-disabled="true" onClick={(e) => e.preventDefault()}>
            {eyeIcon}
          </button>
        </TooltipTrigger>
        <TooltipContent>{lockTooltip}</TooltipContent>
      </Tooltip>
    );
  } else {
    const eyeButton = (
      <button type="button" className={TW_OrganizeFieldsEye} aria-label={eyeLabel} onClick={onToggle} disabled={!toggleable}>
        {eyeIcon}
      </button>
    );
    // What a click does (a disabled eye gets no tooltip: the dialog says why)
    eye = toggleable
      ? <Tooltip>
        <TooltipTrigger asChild>{eyeButton}</TooltipTrigger>
        <TooltipContent>{t(row.visible ? "view.header.organize.fields.hide.field.tooltip" : "view.header.organize.fields.show.field.tooltip")}</TooltipContent>
      </Tooltip>
      : eyeButton;
  }

  return (
    <li className={TW_OrganizeFieldsRow + (dragging ? " " + TW_OrganizeFieldsRowDragging : "")} {...rowDragProps}>
      {dropPosition && <div
        className={TW_OrganizeFieldsDropLine}
        style={{
          ...(dropPosition === 'before' ? { top: -1 } : { bottom: -1 }),
          backgroundColor: dropLineColor,
          boxShadow: `0 0 4px ${dropLineColor}`,
        }}
      />}
      {rowDragProps
        ? <GripVertical className={TW_OrganizeFieldsGrip} aria-hidden="true" />
        : <span className={TW_OrganizeFieldsGripPlaceholder} />}
      {!isObject && eye}
      {row.isError
        // No eye in the configuration of an object: the warning takes the place of the aggregation
        ? (isObject ? <TriangleAlert className={TW_OrganizeFieldsIcon + " text-amber-500"} aria-hidden="true" /> : <span className={TW_OrganizeFieldsGripPlaceholder} />)
        : isObject
          ? <OrganizeFieldsDefaultAggregation row={row} aggregation={aggregation} />
          : <OrganizeFieldsAggregation row={row} aggregation={aggregation} disabled={!canEdit} onAggregationChange={onAggregationChange} />}
      <span className={TW_OrganizeFieldsSeparator} aria-hidden="true" />
      <TypeIcon className={TW_OrganizeFieldsTypeIcon} aria-hidden="true" />
      <span
        className={(struck ? TW_OrganizeFieldsNameHidden : TW_OrganizeFieldsName) + (toggleable ? " cursor-pointer" : "")}
        title={name}
        onClick={toggleable ? onToggle : undefined}
      >
        {name}
      </span>
      {row.isError && <span className={TW_OrganizeFieldsNameSuffix}>({t("group.field.invalid")})</span>}
    </li>
  );
}


interface OrganizeFieldsAggregationProps {
  row: OrganizeFieldRow;
  aggregation: AggregationType | null;
  disabled: boolean;
  onAggregationChange: (aggregation: AggregationType | null) => void;
}

// Icon of the aggregation applied to a field; it opens the aggregations the type
// of the field allows, as the menu of its column does (the choice goes to the draft)
function OrganizeFieldsAggregation({ row, aggregation, disabled, onAggregationChange }: OrganizeFieldsAggregationProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { type, typeItems } = row.field;
  const aggregations = useMemo(() => validAggregationByType(type, typeItems), [type, typeItems]);
  const isLinkColumn = type === FieldType.Link;
  const aggregationName = t(`field.aggregation.label.${aggregation}`);
  // No aggregation (none set, or "no aggregation"): a dimmed dash, so that the
  // fields which aggregate their values stand out in the list
  const isNone = aggregation === null || aggregation === AggregationType.NO_AGGR;

  const onSelect = (selected: AggregationType | null) => {
    onAggregationChange(selected);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={isNone ? TW_OrganizeFieldsAggregationNone : TW_OrganizeFieldsEye}
              aria-label={translate(t, "view.header.organize.fields.aggregation.2", [row.field.name, aggregationName])}
              disabled={disabled}
            >
              {isNone ? <Minus className={TW_OrganizeFieldsIcon} /> : aggregationTypeToIcon(aggregation, TW_OrganizeFieldsIcon)}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t("view.header.organize.fields.aggregation.tooltip")}</TooltipContent>
      </Tooltip>
      <PopoverContent className={TW_FieldMenuPopoverContent}>
        <p className={TW_OrganizeFieldsAggregationTitle}>{t("field.view.menu.aggregation")}</p>
        <ul>
          {aggregations.map(option => (
            <AggregationMenuItem key={option} aggregationType={option} onAggregationChange={onSelect} currentAggregation={aggregation} isLinkColumn={isLinkColumn} />
          ))}
          <AggregationMenuItem key="default" aggregationType={null} onAggregationChange={onSelect} currentAggregation={aggregation} isLinkColumn={isLinkColumn} />
        </ul>
      </PopoverContent>
    </Popover>
  );
}


interface OrganizeFieldsDefaultAggregationProps {
  row: OrganizeFieldRow;
  aggregation: AggregationType | null;
}

// Default aggregation of a field, in the configuration of an object: shown, not
// changed here. Its tooltip tells where it changes: on the field itself for a
// link (the only type whose form has the setting), in the views otherwise.
function OrganizeFieldsDefaultAggregation({ row, aggregation }: OrganizeFieldsDefaultAggregationProps) {
  const { t } = useTranslation();
  const aggregationName = t(`field.aggregation.label.${aggregation}`);
  const isNone = aggregation === null || aggregation === AggregationType.NO_AGGR;
  const tooltip = row.field.type === FieldType.Link
    ? t("view.header.organize.fields.aggregation.link.tooltip")
    : t("view.header.organize.fields.aggregation.view.tooltip");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* aria-disabled rather than disabled: the icon stays focusable, and its tooltip reachable */}
        <button
          type="button"
          className={isNone ? TW_OrganizeFieldsAggregationNone : TW_OrganizeFieldsEye}
          aria-label={translate(t, "view.header.organize.fields.aggregation.2", [row.field.name, aggregationName])}
          aria-disabled="true"
          onClick={(e) => e.preventDefault()}
        >
          {isNone ? <Minus className={TW_OrganizeFieldsIcon} /> : aggregationTypeToIcon(aggregation, TW_OrganizeFieldsIcon)}
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
